import { Observable, of } from 'rxjs';
import { first, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { API_ENDPOINTS } from '../../type-api-endpoint-mapping';
import { Expression, ExpressionDataStore, expressionSelectors } from '../expression';
import { Item, ItemDataStore, itemSelectors } from '../item';
import { Manifestation, ManifestationDataStore, manifestationSelectors } from '../manifestation';
import { Work, WorkDataStore } from '../work';

/**
 * A Wemi is an object gathering 
 * Work,
 * Expression,
 * Manifestation,
 * Item
 * datas for a given item 
 */
// tslint:disable-next-line:no-empty-interface
export interface Wemi {
    item: Item | null,
    manifestation: Manifestation | null,
    expression: Expression | null,
    work: Work | null;
}

export class WemiUtils {

    /**
     * 
     * @param itemId Builds a full Wemi object from the item id
     */
    public static getWemiFromItem(itemId: string): Observable<Wemi> {
        return ItemDataStore.getInstance().fetchOne(itemId)
            .pipe(
                map(item => ({ item })),
                switchMap(
                    wemi => {
                        if (wemi.item) {
                            const manifestationId = itemSelectors.manifestation(wemi.item);
                            if (manifestationId) {
                                return ManifestationDataStore.getInstance().fetchOne(manifestationId).pipe(
                                    map(manifestation => ({ ...wemi, manifestation }))
                                )
                            }
                        }
                        return of({ ...wemi, manifestation: null })
                    }
                ),
                switchMap(
                    wemi => {
                        if (wemi.manifestation) {
                            const expressionId = manifestationSelectors.expression(wemi.manifestation);
                            if (expressionId) {
                                return ExpressionDataStore.getInstance().fetchOne(expressionId).pipe(
                                    map(expression => ({ ...wemi, expression }))
                                )
                            }
                        }
                        return of({ ...wemi, expression: null })
                    }
                ),
                mergeMap(
                    wemi => {
                        if (wemi.expression) {
                            const workId = expressionSelectors.work(wemi.expression);
                            if (workId) {
                                return WorkDataStore.getInstance().fetchOne(workId).pipe(
                                    map(work => ({ ...wemi, work }) as Wemi))
                            }
                        }
                        return of({ ...wemi, work: null } as Wemi)
                    }
                )
            )
    }

    public static async saveWemi(wemi: Wemi): Promise<any> {
        return Promise.all(
            [
                // Save Work 
                wemi.work ? WorkDataStore.getInstance()
                    .update(wemi.work.id, wemi.work, API_ENDPOINTS[wemi.work.type])
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Save Expression
                wemi.expression ? ExpressionDataStore.getInstance()
                    .update(wemi.expression.id, wemi.expression, API_ENDPOINTS[wemi.expression.type])
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Save Manifestation
                wemi.manifestation ? ManifestationDataStore.getInstance()
                    .update(wemi.manifestation.id, wemi.manifestation, API_ENDPOINTS[wemi.manifestation.type])
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Save Item
                wemi.item ? ItemDataStore.getInstance()
                    .update(wemi.item.id, wemi.item, API_ENDPOINTS[wemi.item.type])
                    .pipe(
                        first()
                    ).toPromise() : null
            ]
        )
    }

}

export function isWemi(wemi: any) {
    return "item" in wemi && "manifestation" in wemi && "expression" in wemi && "work" in wemi;
}